/**
 * @tanstack/react-query bindings for the Accounting module (G_ACC3a —
 * contacts + items master CRUD).
 *
 * Thin wrappers over lib/api/accounting.ts so pages stay declarative and
 * caching/invalidation lives in one place. Mirrors the use-offboarding.ts
 * conventions:
 *
 *  - Query-key convention:  ["accounting", <area>, ...params]
 *  - Mutations invalidate the relevant LIST bucket AND the specific row
 *    they touched (so a detail page re-renders after an update / archive
 *    in the same tick).
 *
 * No optimistic updates in v1 — every mutation server-side does an
 * audit_events write in the same TX, so the success of the request is
 * the moment we should fetch fresh. (Optimistic flip on archive is a
 * tempting win but a 409 on a concurrent edit would race the cache; the
 * current refetch path is fast enough for the master-data table.)
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { accountingApi } from "@/lib/api";
import { invalidateCompanyProfile } from "@/lib/company-profile-queries";
import type {
  AccountType,
  BillStatus,
  ContactKind,
  CreateBillRequest,
  CreateContactRequest,
  CreateInvoiceRequest,
  CreateItemRequest,
  CreatePaymentAccountRequest,
  CreatePaymentRequest,
  CreatePeriodLockRequest,
  CreateQuoteRequest,
  CreateReceiptRequest,
  CreateTaxCodeRequest,
  GenerateTaxReturnRequest,
  InvoiceStatus,
  ItemKind,
  ListAuditEventsParams,
  PaymentAccountKind,
  PaymentStatus,
  QuoteStatus,
  ReceiptStatus,
  TaxCodeKind,
  TaxReturnType,
  TransitionQuoteRequest,
  UpdateBillRequest,
  UpdateContactRequest,
  UpdateInvoiceRequest,
  UpdateItemRequest,
  UpdatePaymentAccountRequest,
  UpdatePaymentRequest,
  UpdateQuoteRequest,
  UpdateReceiptRequest,
  UpdateTaxCodeRequest,
  UpsertTallyMappingRequest,
  UpdateGstProfileRequest,
} from "@/types/accounting";

export const accountingKeys = {
  all: ["accounting"] as const,
  // Company GST profile (GST state + GSTIN).
  gstProfile: () => [...accountingKeys.all, "gst-profile"] as const,
  // Contacts
  contacts: (params?: {
    kind?: ContactKind;
    search?: string;
    gstin?: string;
    include_archived?: boolean;
  }) =>
    [
      ...accountingKeys.all,
      "contacts",
      params?.kind ?? "any",
      params?.search ?? "",
      params?.gstin ?? "",
      params?.include_archived ?? false,
    ] as const,
  contact: (id: number) =>
    [...accountingKeys.all, "contact", id] as const,
  // Items
  items: (params?: {
    kind?: ItemKind;
    search?: string;
    include_archived?: boolean;
  }) =>
    [
      ...accountingKeys.all,
      "items",
      params?.kind ?? "any",
      params?.search ?? "",
      params?.include_archived ?? false,
    ] as const,
  item: (id: number) => [...accountingKeys.all, "item", id] as const,
  // Accounts (G_ACC5 — chart of accounts list)
  accounts: (params?: { type?: AccountType; include_archived?: boolean }) =>
    [
      ...accountingKeys.all,
      "accounts",
      params?.type ?? "any",
      params?.include_archived ?? false,
    ] as const,
  // Invoices
  invoices: (params?: {
    status?: InvoiceStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  }) =>
    [
      ...accountingKeys.all,
      "invoices",
      params?.status ?? "any",
      params?.contact_id ?? 0,
      params?.date_from ?? "",
      params?.date_to ?? "",
    ] as const,
  invoice: (id: number) =>
    [...accountingKeys.all, "invoice", id] as const,
  // Quotes
  quotes: (params?: { status?: QuoteStatus; contact_id?: number }) =>
    [
      ...accountingKeys.all,
      "quotes",
      params?.status ?? "any",
      params?.contact_id ?? 0,
    ] as const,
  quote: (id: number) => [...accountingKeys.all, "quote", id] as const,
  // Bills
  bills: (params?: {
    status?: BillStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  }) =>
    [
      ...accountingKeys.all,
      "bills",
      params?.status ?? "any",
      params?.contact_id ?? 0,
      params?.date_from ?? "",
      params?.date_to ?? "",
    ] as const,
  bill: (id: number) => [...accountingKeys.all, "bill", id] as const,
  // Payment accounts (G_ACC6c — wallets master)
  paymentAccounts: (params?: {
    kind?: PaymentAccountKind;
    include_inactive?: boolean;
  }) =>
    [
      ...accountingKeys.all,
      "payment-accounts",
      params?.kind ?? "any",
      params?.include_inactive ?? false,
    ] as const,
  paymentAccount: (id: number) =>
    [...accountingKeys.all, "payment-account", id] as const,
  // Receipts (G_ACC6c — AR intake)
  receipts: (params?: {
    status?: ReceiptStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  }) =>
    [
      ...accountingKeys.all,
      "receipts",
      params?.status ?? "any",
      params?.contact_id ?? 0,
      params?.date_from ?? "",
      params?.date_to ?? "",
    ] as const,
  receipt: (id: number) =>
    [...accountingKeys.all, "receipt", id] as const,
  // Payments (G_ACC6c — AP disburse + TDS)
  payments: (params?: {
    status?: PaymentStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  }) =>
    [
      ...accountingKeys.all,
      "payments",
      params?.status ?? "any",
      params?.contact_id ?? 0,
      params?.date_from ?? "",
      params?.date_to ?? "",
    ] as const,
  payment: (id: number) =>
    [...accountingKeys.all, "payment", id] as const,
  // Journal entries (read surface — list + detail)
  journalEntries: (params?: {
    source_type?: string;
    date_from?: string;
    date_to?: string;
  }) =>
    [
      ...accountingKeys.all,
      "journal-entries",
      params?.source_type ?? "any",
      params?.date_from ?? "",
      params?.date_to ?? "",
    ] as const,
  journalEntry: (id: number) =>
    [...accountingKeys.all, "journal-entry", id] as const,
  // Tally ledger mappings (CR0.2 — Tally XML export)
  tallyMappings: () =>
    [...accountingKeys.all, "tally-mappings"] as const,
};

/**
 * After every mutation we invalidate the whole accounting tree —
 * the master-data tables are small (few hundred rows per tenant at
 * most), the wire payload is cheap, and the alternative (per-key
 * surgical invalidation) leaks the mutation surface across many call
 * sites with no perf win in practice. Mirrors offboarding invalidateAll.
 */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: accountingKeys.all });
}

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

export function useContacts(params?: {
  kind?: ContactKind;
  search?: string;
  gstin?: string;
  include_archived?: boolean;
}) {
  return useQuery({
    queryKey: accountingKeys.contacts(params),
    queryFn: ({ signal }) => accountingApi.listContacts(params, signal),
  });
}

export function useContact(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.contact(id),
    queryFn: ({ signal }) => accountingApi.getContact(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContactRequest) =>
      accountingApi.createContact(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateContactRequest }) =>
      accountingApi.updateContact(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchiveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.archiveContact(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export function useItems(params?: {
  kind?: ItemKind;
  search?: string;
  include_archived?: boolean;
}) {
  return useQuery({
    queryKey: accountingKeys.items(params),
    queryFn: ({ signal }) => accountingApi.listItems(params, signal),
  });
}

export function useItem(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.item(id),
    queryFn: ({ signal }) => accountingApi.getItem(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateItemRequest) =>
      accountingApi.createItem(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateItemRequest }) =>
      accountingApi.updateItem(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchiveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.archiveItem(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// Accounts (G_ACC5 list + G_ACC2 CRUD)
// ---------------------------------------------------------------------------

/**
 * GET /accounting/accounts — chart of accounts list. The line editor
 * uses ?type=income for the sales-line revenue-head picker and
 * ?type=expense for the bill-line expense-head picker. The G_ACC2 COA
 * admin surface passes include_archived=true to see archived custom accounts.
 */
export function useAccounts(params?: {
  type?: AccountType;
  include_archived?: boolean;
}) {
  return useQuery({
    queryKey: accountingKeys.accounts(params),
    queryFn: ({ signal }) => accountingApi.listAccounts(params, signal),
  });
}

/**
 * GET /accounting/accounts-with-balances — browsable Chart of Accounts
 * WITH running ledger balances (Sprint 4 §A). Distinct cache row per
 * include_archived value.
 */
export function useAccountsWithBalances(params?: {
  include_archived?: boolean;
}) {
  return useQuery({
    queryKey: [
      ...accountingKeys.all,
      "accounts-with-balances",
      params?.include_archived ?? false,
    ] as const,
    queryFn: ({ signal }) =>
      accountingApi.getAccountsWithBalances(params, signal),
  });
}

/** GET /accounting/accounts/{id} — single account (G_ACC2 detail). */
export function useAccount(id: number, enabled = true) {
  return useQuery({
    queryKey: [...accountingKeys.all, "account", id] as const,
    queryFn: ({ signal }) => accountingApi.getAccount(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

/** POST /accounting/accounts — create a custom sub-account (G_ACC2). */
export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@/lib/api/accounting").CreateAccountRequest) =>
      accountingApi.createAccount(body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** PATCH /accounting/accounts/{id} — rename / update description (G_ACC2). */
export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: import("@/lib/api/accounting").UpdateAccountRequest;
    }) => accountingApi.updateAccount(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** POST /accounting/accounts/{id}/archive — soft-archive (G_ACC2). */
export function useArchiveAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.archiveAccount(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// Invoices (6 mutations + 2 queries)
// ---------------------------------------------------------------------------

export function useInvoices(params?: {
  status?: InvoiceStatus;
  contact_id?: number;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: accountingKeys.invoices(params),
    queryFn: ({ signal }) => accountingApi.listInvoices(params, signal),
  });
}

export function useInvoice(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.invoice(id),
    queryFn: ({ signal }) => accountingApi.getInvoice(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvoiceRequest) =>
      accountingApi.createInvoice(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateInvoiceRequest }) =>
      accountingApi.updateInvoice(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Draft → sent + engine.Post. Irreversible. */
export function useSendInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.sendInvoice(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Email the invoice PDF to the customer contact (POST …/email). */
export function useEmailInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.emailInvoice(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Cancel — pure from draft, engine.Void from sent / partly_paid. */
export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; reason?: string }) =>
      accountingApi.cancelInvoice(vars.id, vars.reason),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// Quotes (6 mutations + 2 queries)
// ---------------------------------------------------------------------------

export function useQuotes(params?: {
  status?: QuoteStatus;
  contact_id?: number;
}) {
  return useQuery({
    queryKey: accountingKeys.quotes(params),
    queryFn: ({ signal }) => accountingApi.listQuotes(params, signal),
  });
}

export function useQuote(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.quote(id),
    queryFn: ({ signal }) => accountingApi.getQuote(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateQuoteRequest) =>
      accountingApi.createQuote(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateQuoteRequest }) =>
      accountingApi.updateQuote(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * State-machine transition (sent / accepted / declined / expired). For
 * accepted → converted use {@link useConvertQuote} instead.
 */
export function useTransitionQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: TransitionQuoteRequest }) =>
      accountingApi.transitionQuote(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * Accepted → converted + child draft invoice. The invoice is created
 * but NOT posted — the caller still has to /invoices/{id}/send.
 */
export function useConvertQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.convertQuote(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * Email the quote PDF to the customer. A draft becomes "sent" server-side,
 * so the detail + list refetch (status pill, email history).
 */
export function useEmailQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.emailQuote(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// Bills (6 mutations + 2 queries)
// ---------------------------------------------------------------------------

export function useBills(params?: {
  status?: BillStatus;
  contact_id?: number;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: accountingKeys.bills(params),
    queryFn: ({ signal }) => accountingApi.listBills(params, signal),
  });
}

export function useBill(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.bill(id),
    queryFn: ({ signal }) => accountingApi.getBill(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreateBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBillRequest) =>
      accountingApi.createBill(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateBillRequest }) =>
      accountingApi.updateBill(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Draft → received + engine.Post (AP direction). Irreversible. */
export function useReceiveBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.receiveBill(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCancelBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; reason?: string }) =>
      accountingApi.cancelBill(vars.id, vars.reason),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// G_ACC6c — Payment accounts (wallets), Receipts (AR), Payments (AP).
// ---------------------------------------------------------------------------

export function usePaymentAccounts(params?: {
  kind?: PaymentAccountKind;
  include_inactive?: boolean;
}) {
  return useQuery({
    queryKey: accountingKeys.paymentAccounts(params),
    queryFn: ({ signal }) => accountingApi.listPaymentAccounts(params, signal),
  });
}

export function usePaymentAccount(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.paymentAccount(id),
    queryFn: ({ signal }) => accountingApi.getPaymentAccount(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreatePaymentAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePaymentAccountRequest) =>
      accountingApi.createPaymentAccount(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdatePaymentAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdatePaymentAccountRequest }) =>
      accountingApi.updatePaymentAccount(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchivePaymentAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.archivePaymentAccount(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// --- Receipts -------------------------------------------------------------

export function useReceipts(params?: {
  status?: ReceiptStatus;
  contact_id?: number;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: accountingKeys.receipts(params),
    queryFn: ({ signal }) => accountingApi.listReceipts(params, signal),
  });
}

export function useReceipt(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.receipt(id),
    queryFn: ({ signal }) => accountingApi.getReceipt(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreateReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReceiptRequest) =>
      accountingApi.createReceipt(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateReceiptRequest }) =>
      accountingApi.updateReceipt(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Draft → posted + engine.Post (AR direction). Irreversible. */
export function usePostReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.postReceipt(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Posted → cancelled + reversing JE. */
export function useVoidReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.voidReceipt(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// --- Payments -------------------------------------------------------------

export function usePayments(params?: {
  status?: PaymentStatus;
  contact_id?: number;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: accountingKeys.payments(params),
    queryFn: ({ signal }) => accountingApi.listPayments(params, signal),
  });
}

export function usePayment(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.payment(id),
    queryFn: ({ signal }) => accountingApi.getPayment(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePaymentRequest) =>
      accountingApi.createPayment(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdatePaymentRequest }) =>
      accountingApi.updatePayment(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Draft → posted + engine.Post (AP direction, 3-line JE when TDS > 0). */
export function usePostPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.postPayment(id),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Posted → cancelled + reversing JE. */
export function useVoidPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.voidPayment(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// G_ACC8 — Reports (read-only). The query keys include the date(s) so a
// date change is a distinct cache row — never serve a stale TB when the
// user advances the as-of picker.
// ---------------------------------------------------------------------------

export const reportKeys = {
  trialBalance: (asOf?: string) =>
    [...accountingKeys.all, "reports", "trial-balance", asOf ?? "today"] as const,
  profitAndLoss: (from: string, to: string) =>
    [...accountingKeys.all, "reports", "profit-and-loss", from, to] as const,
  balanceSheet: (asOf?: string) =>
    [...accountingKeys.all, "reports", "balance-sheet", asOf ?? "today"] as const,
  msmeOverdue: (asOf?: string) =>
    [...accountingKeys.all, "reports", "msme-overdue", asOf ?? "today"] as const,
  dashboard: (asOf?: string) =>
    [...accountingKeys.all, "reports", "dashboard", asOf ?? "today"] as const,
  tdsReceivable: (from: string, to: string) =>
    [...accountingKeys.all, "reports", "tds-receivable", from, to] as const,
};

export function useTrialBalanceReport(asOf?: string) {
  return useQuery({
    queryKey: reportKeys.trialBalance(asOf),
    queryFn: ({ signal }) => accountingApi.getTrialBalance(asOf, signal),
  });
}

export function useProfitAndLossReport(from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.profitAndLoss(from, to),
    queryFn: ({ signal }) => accountingApi.getProfitAndLoss(from, to, signal),
    enabled: Boolean(from) && Boolean(to) && from <= to,
  });
}

export function useBalanceSheetReport(asOf?: string) {
  return useQuery({
    queryKey: reportKeys.balanceSheet(asOf),
    queryFn: ({ signal }) => accountingApi.getBalanceSheet(asOf, signal),
  });
}

export function useMSMEOverdueReport(asOf?: string) {
  return useQuery({
    queryKey: reportKeys.msmeOverdue(asOf),
    queryFn: ({ signal }) => accountingApi.getMSMEOverdue(asOf, signal),
  });
}

/** TDS receivable / 26AS reconciliation for an inclusive date range. */
export function useTDSReceivableReport(from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.tdsReceivable(from, to),
    queryFn: ({ signal }) =>
      accountingApi.getTDSReceivable({ from, to }, signal),
    enabled: Boolean(from) && Boolean(to) && from <= to,
  });
}

export function useAccountingDashboardWidget(asOf?: string, enabled = true) {
  return useQuery({
    queryKey: reportKeys.dashboard(asOf),
    queryFn: ({ signal }) => accountingApi.getAccountingDashboard(asOf, signal),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// G_ACC9 — audit-events viewer + period-locks admin.
// ---------------------------------------------------------------------------

export const auditEventKeys = {
  list: (params?: ListAuditEventsParams) =>
    [
      ...accountingKeys.all,
      "audit-events",
      params?.entity_type ?? "any",
      params?.entity_id ?? 0,
      params?.actor_id ?? 0,
      params?.action ?? "any",
      params?.from ?? "",
      params?.to ?? "",
      params?.limit ?? 50,
      params?.offset ?? 0,
    ] as const,
};

export const periodLockKeys = {
  list: () => [...accountingKeys.all, "period-locks"] as const,
};

export function useAuditEvents(params?: ListAuditEventsParams) {
  return useQuery({
    queryKey: auditEventKeys.list(params),
    queryFn: ({ signal }) => accountingApi.listAuditEvents(params, signal),
  });
}

export function usePeriodLocks() {
  return useQuery({
    queryKey: periodLockKeys.list(),
    queryFn: ({ signal }) => accountingApi.listPeriodLocks(signal),
  });
}

export function useCreatePeriodLock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePeriodLockRequest) =>
      accountingApi.createPeriodLock(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: periodLockKeys.list() });
      void qc.invalidateQueries({ queryKey: accountingKeys.all });
    },
  });
}

export function useDeletePeriodLock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { periodEnd: string; reason?: string }) =>
      accountingApi.deletePeriodLock(vars.periodEnd, vars.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: periodLockKeys.list() });
      void qc.invalidateQueries({ queryKey: accountingKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// G_ACC_CR2 — Opening Balance CSV importer.
//
// Three hooks back the wizard:
//   - useOpeningBalanceImports   — list/history card (read-only).
//   - usePreviewOpeningBalance   — mutation; the response (200 OR 422)
//                                  feeds the preview table.
//   - useCommitOpeningBalance    — mutation; invalidates the imports
//                                  list + the whole accounting tree
//                                  so trial balance / reports refetch
//                                  on the success page.
// ---------------------------------------------------------------------------

import type {
  OpeningBalanceCommitResponse,
  OpeningBalanceImport,
  OpeningBalancePreviewResult,
  ListOpeningBalanceImportsResponse,
} from "@/types/opening-balance";

export const openingBalanceKeys = {
  all: [...accountingKeys.all, "opening-balance"] as const,
  list: () => [...openingBalanceKeys.all, "list"] as const,
  detail: (id: number) =>
    [...openingBalanceKeys.all, "detail", id] as const,
};

/** GET /api/v1/accounting/opening-balance/imports */
export function useOpeningBalanceImports() {
  return useQuery<ListOpeningBalanceImportsResponse>({
    queryKey: openingBalanceKeys.list(),
    queryFn: ({ signal }) => accountingApi.listOpeningBalanceImports(signal),
  });
}

/** GET /api/v1/accounting/opening-balance/imports/{id} */
export function useOpeningBalanceImport(id: number, enabled = true) {
  return useQuery<OpeningBalanceImport>({
    queryKey: openingBalanceKeys.detail(id),
    queryFn: ({ signal }) =>
      accountingApi.getOpeningBalanceImport(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

/**
 * Preview mutation. Resolves with the preview body on BOTH 200 (clean)
 * and 422 (per-row errors) — the api wrapper unwraps the 422 envelope
 * so the wizard can render the rows + errors inline without throwing.
 * Genuine network / 5xx errors still reject for the toast path.
 */
export function usePreviewOpeningBalance() {
  return useMutation<
    OpeningBalancePreviewResult,
    Error,
    { file: File; asOfDate: string }
  >({
    mutationFn: ({ file, asOfDate }) =>
      accountingApi.previewOpeningBalance(file, asOfDate),
  });
}

/**
 * Commit mutation. Invalidates the imports list AND the whole
 * accounting tree — committing an opening balance writes a JE that
 * shows up in trial balance, P&L, balance sheet, audit-trail, etc.
 * The wizard's success step navigates back to /dashboard/accounting,
 * which uses the now-fresh data.
 */
export function useCommitOpeningBalance() {
  const qc = useQueryClient();
  return useMutation<
    OpeningBalanceCommitResponse,
    Error,
    { file: File; asOfDate: string }
  >({
    mutationFn: ({ file, asOfDate }) =>
      accountingApi.commitOpeningBalance(file, asOfDate),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: openingBalanceKeys.list() });
      void qc.invalidateQueries({ queryKey: accountingKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Tax codes (G_ACC3 — accounting_tax_codes master CRUD).
// ---------------------------------------------------------------------------

export const taxCodeKeys = {
  list: (params?: { kind?: TaxCodeKind; include_inactive?: boolean }) =>
    [
      ...accountingKeys.all,
      "tax-codes",
      params?.kind ?? "any",
      params?.include_inactive ?? false,
    ] as const,
  detail: (id: number) =>
    [...accountingKeys.all, "tax-code", id] as const,
};

export function useTaxCodes(params?: {
  kind?: TaxCodeKind;
  include_inactive?: boolean;
}) {
  return useQuery({
    queryKey: taxCodeKeys.list(params),
    queryFn: ({ signal }) => accountingApi.listTaxCodes(params, signal),
  });
}

export function useCreateTaxCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaxCodeRequest) =>
      accountingApi.createTaxCode(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateTaxCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateTaxCodeRequest }) =>
      accountingApi.updateTaxCode(vars.id, vars.body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useArchiveTaxCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => accountingApi.archiveTaxCode(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---------------------------------------------------------------------------
// G_ACC7 — Tax returns (GST + TDS).
// ---------------------------------------------------------------------------

/**
 * Lists all generated tax returns, optionally filtered by type and/or year.
 * Query key: ["accounting", "tax-returns", returnType ?? "all", year ?? 0]
 */
export function useListTaxReturns(params?: {
  return_type?: TaxReturnType;
  year?: number;
}) {
  return useQuery({
    queryKey: [
      "accounting",
      "tax-returns",
      params?.return_type ?? "all",
      params?.year ?? 0,
    ],
    queryFn: ({ signal }) => accountingApi.listTaxReturns(params, signal),
  });
}

/**
 * Fetches a single tax return by id (includes snapshot_jsonb).
 */
export function useGetTaxReturn(id: number | null) {
  return useQuery({
    queryKey: ["accounting", "tax-return", id],
    queryFn: ({ signal }) => accountingApi.getTaxReturn(id!, signal),
    enabled: id !== null,
  });
}

/**
 * Generates a new tax return (POST /accounting/tax-returns).
 * On success, invalidates all tax-return list queries.
 */
export function useGenerateTaxReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GenerateTaxReturnRequest) =>
      accountingApi.generateTaxReturn(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accounting", "tax-returns"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Manual journal entries (Sprint 4 §C).
//
// A posted manual journal writes to the ledger immediately, so on
// success we invalidate the WHOLE accounting tree — the trial balance,
// reports, accounts-with-balances and audit-trail all move.
// ---------------------------------------------------------------------------

export function useCreateManualJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@/types/accounting").CreateManualJournalRequest) =>
      accountingApi.createManualJournal(body),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * GET /accounting/journal-entries — posted journal entries list. The
 * query key includes every filter so a filter change is a distinct cache
 * row (mirrors the invoices/bills list hooks).
 */
export function useJournalEntries(params?: {
  source_type?: string;
  date_from?: string;
  date_to?: string;
}) {
  return useQuery({
    queryKey: accountingKeys.journalEntries(params),
    queryFn: ({ signal }) => accountingApi.listJournalEntries(params, signal),
  });
}

/** GET /accounting/journal-entries/{id} — header + lines + totals. */
export function useJournalEntry(id: number, enabled = true) {
  return useQuery({
    queryKey: accountingKeys.journalEntry(id),
    queryFn: ({ signal }) => accountingApi.getJournalEntry(id, signal),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

// ---------------------------------------------------------------------------
// CR0.2 — Tally ledger mappings (settings surface for the Tally XML export).
// ---------------------------------------------------------------------------

/**
 * GET /accounting/tally-mappings — every mappable ledger, resolved.
 * `enabled` false skips the fetch (the settings page uses the payroll-only
 * endpoint instead when the Accounting module is off — wave 2).
 */
export function useTallyMappings(enabled = true) {
  return useQuery({
    queryKey: accountingKeys.tallyMappings(),
    queryFn: ({ signal }) => accountingApi.listTallyMappings(signal),
    enabled,
  });
}

/**
 * PUT /accounting/tally-mappings — upsert (or clear, via an empty
 * tally_ledger_name) one row's mapping. Resolves with the updated row
 * so the settings page can reset per-row local state immediately; the
 * list key is invalidated on success so any other consumer refetches.
 */
export function useUpsertTallyMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertTallyMappingRequest) =>
      accountingApi.upsertTallyMapping(body),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: accountingKeys.tallyMappings(),
      });
    },
  });
}

/**
 * Company GST profile (GST state + GSTIN). Drives the invoice/bill
 * place-of-supply default and the "set your GST state" prompt. Callers
 * that change the profile (PATCH /tenant/settings) invalidate
 * accountingKeys.gstProfile().
 */
export function useGstProfile() {
  return useQuery({
    queryKey: accountingKeys.gstProfile(),
    queryFn: ({ signal }) => accountingApi.getGstProfile(signal),
    staleTime: 60_000,
  });
}

/**
 * PATCH /accounting/gst-profile — set the company GSTIN / GST state from
 * the books (every accounting role, incl. the accountant).
 */
export function useUpdateGstProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateGstProfileRequest) => accountingApi.updateGstProfile(body),
    onSuccess: async (data) => {
      qc.setQueryData(accountingKeys.gstProfile(), data);
      // Settings → Workspace and the Company profile card show the same
      // GSTIN / state (usability review 2026-10-20: "Not set" until a
      // reload).
      await invalidateCompanyProfile(qc);
    },
  });
}

/**
 * GET /accounting/tax-returns/preview — the numbers Generate would freeze
 * right now, plus the drafts it would leave out. Nothing is saved.
 */
export function useTaxReturnPreview(
  params: { return_type: TaxReturnType; period_year: number; period_index: number } | null,
) {
  return useQuery({
    queryKey: [
      "accounting",
      "tax-return-preview",
      params?.return_type ?? "",
      params?.period_year ?? 0,
      params?.period_index ?? 0,
    ],
    queryFn: ({ signal }) => accountingApi.previewTaxReturn(params!, signal),
    enabled: params !== null,
  });
}

/**
 * GET /accounting/bills/tds-preview — the TDS the bill form's values would
 * withhold, from the server (the same function the bill is saved with).
 * null params = nothing to preview (no section or no amount yet).
 */
export function useBillTDSPreview(
  params: { section: string; subtotal_minor: number; contact_id?: number } | null,
) {
  return useQuery({
    queryKey: [
      "accounting",
      "bill-tds-preview",
      params?.section ?? "",
      params?.subtotal_minor ?? 0,
      params?.contact_id ?? 0,
    ],
    queryFn: ({ signal }) => accountingApi.previewBillTDS(params!, signal),
    enabled: params !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** GET /accounting/accounts/{id}/ledger — the account ledger (drill-down). */
export function useAccountLedger(id: number, params: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ["accounting", "account-ledger", id, params.from ?? "", params.to ?? ""],
    queryFn: ({ signal }) => accountingApi.getAccountLedger(id, params, signal),
    enabled: Number.isFinite(id) && id > 0,
  });
}

/** GET /accounting/payroll-runs — locked payroll runs (read-only register). */
export function usePayrollRegisterRuns() {
  return useQuery({
    queryKey: ["accounting", "payroll-register"],
    queryFn: ({ signal }) => accountingApi.listPayrollRegister(signal),
  });
}

/** GET /accounting/payroll-runs/{id} — one locked run's register. */
export function usePayrollRegisterRun(id: number) {
  return useQuery({
    queryKey: ["accounting", "payroll-register", id],
    queryFn: ({ signal }) => accountingApi.getPayrollRegister(id, signal),
    enabled: Number.isFinite(id) && id > 0,
  });
}
